// components/Credibility/CredibilityChip.jsx
//
// 2026-07-19 credibility-chip consolidation. Replaces 5 independent,
// inconsistent "show a user's credibility" implementations across the app
// (raw-number-only post badge, tier+accuracy leaderboard subline, tier-only
// news bylines, and two dead components below) with one shared source of
// truth, used everywhere a username needs its credibility shown.
//
// The two prior components this replaces both had real bugs, not just
// duplication:
//  - credibilityPill.jsx: colored its star icon by hardcoded score bands
//    (>=200/>=100/else) that don't match the real ~900-1000 point scale,
//    and never rendered the real tier as visible text (tooltip only).
//  - CredibilityPostPill.jsx: computed its own FAKE tier from a raw score
//    via a local tierFor() with entirely made-up band names ("Elite/
//    Trusted/Developing/Noisy") on an assumed 0-100 scale -- unrelated to
//    the real backend tier system (Beginner/Active/Advanced/Elite/Top
//    Emperor, derive_status_tier() in credibility_service.py) and the
//    exact same anti-pattern already found and fixed once this session in
//    credibilityChipsComponent.jsx. Neither is imported anywhere in the
//    codebase (confirmed via repo-wide grep before deleting both).
import React from "react";
import { useTranslation } from "react-i18next";
import { Star, HelpCircle } from "lucide-react";
import { tierColors } from "../../utils/credibilityTiers";
import Tooltip from "../../pages/Dashboard/UnifiedDashboard/Tooltip";

const SIZE_CLASSES = {
  xs: "text-[10px] px-1.5 py-0.5 gap-1",
  sm: "text-xs px-2 py-0.5 gap-1.5",
  md: "text-sm px-2.5 py-1 gap-2",
};

const ICON_SIZE = { xs: 10, sm: 12, md: 14 };

/**
 * Props
 * - cred: { score:number, tier:string, accuracyPct?:number, transparency?:string } | null | undefined
 *   `null`/`undefined` means "no CredibilityScore row yet" -- renders a
 *   distinct "Unranked" chip, never conflated with a genuine score of 0.
 * - size: "xs" | "sm" | "md" (default "sm")
 * - showAccuracy: boolean (default true) -- only shown when cred.accuracyPct is a number
 * - showTier: boolean (default true) -- visible tier text, not just the tooltip
 * - className: string
 */
export default function CredibilityChip({
  cred,
  size = "sm",
  showAccuracy = true,
  showTier = true,
  className = "",
}) {
  const { t } = useTranslation();
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.sm;
  const iconSize = ICON_SIZE[size] || ICON_SIZE.sm;

  if (!cred) {
    return (
      <Tooltip content={t('credibility.chip.unrankedTooltip')}>
        <span
          className={`inline-flex items-center rounded-full border bg-gray-500/10 border-gray-400 dark:border-gray-500/30 text-gray-600 dark:text-gray-400 ${sizeClass} ${className}`}
        >
          <HelpCircle size={iconSize} className="opacity-80" />
          <span className="font-medium">{t('credibility.chip.unranked')}</span>
        </span>
      </Tooltip>
    );
  }

  const score = Number.isFinite(cred.score) ? cred.score : 0;
  const tier = cred.tier || "Beginner";
  const acc = Number.isFinite(cred.accuracyPct) ? cred.accuracyPct : null;
  const colors = tierColors(tier);

  const tooltipLines = [
    t('credibility.chip.tooltipScore', { tier, score: Math.round(score) }),
    acc != null ? t('credibility.chip.tooltipAccuracy', { acc: Math.round(acc) }) : null,
    cred.transparency ? t('credibility.chip.tooltipTransparency', { value: cred.transparency }) : null,
  ].filter(Boolean);

  return (
    <Tooltip content={tooltipLines.join(" — ")}>
      <span
        className={`inline-flex items-center rounded-full border backdrop-blur ${colors.bg} ${colors.border} ${colors.text} ${sizeClass} ${className}`}
        aria-label={
          acc != null
            ? t('credibility.chip.ariaLabelWithAccuracy', { tier, score: Math.round(score), acc: Math.round(acc) })
            : t('credibility.chip.ariaLabel', { tier, score: Math.round(score) })
        }
      >
        <Star size={iconSize} className="opacity-90" />
        {showTier && <span className="font-semibold">{tier}</span>}
        <span className="font-medium tabular-nums opacity-90">{Math.round(score)}</span>
        {showAccuracy && acc != null && (
          <span className="opacity-70 tabular-nums">({Math.round(acc)}%)</span>
        )}
      </span>
    </Tooltip>
  );
}
